import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Stack, Button, Breadcrumbs, Link, Typography, ToggleButtonGroup, ToggleButton, Divider } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useTranslation } from 'react-i18next';
import Toolbox, { type ToolboxObjectType } from '@/components/labelDesigner/Toolbox';
import LabelCanvas from '@/components/labelDesigner/LabelCanvas';
import PropertiesPanel from '@/components/labelDesigner/PropertiesPanel';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { apiClient } from '@/api/axiosClient';
import { DEFAULT_AUTO_GENERATE_CONFIG } from '@/lib/autoGenerate';
import { useAuth } from '@/contexts/AuthContext';
import { PERMISSIONS } from '@/types/auth';
import type { ApprovalInstanceDto } from '@/types/approvals';
import type { DistributiveOmit, LabelObject, LabelTemplate } from '@/types/labelTemplate';

// Not a module-level counter that restarts at "obj-1" on every page load: editing an *existing*
// template loads objects whose ids were assigned the same way in a previous session, so a fresh
// counter starting over from 1 would collide with one of them the moment a new object is added.
const nextObjectId = () => `obj-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

function createDefaultObject(type: ToolboxObjectType): DistributiveOmit<LabelObject, 'id' | 'x' | 'y'> {
  switch (type) {
    case 'barcode':
      return { type: 'barcode', w: 60, h: 20, symbology: 'CODE128', data: '{{MaterialCode}}', humanReadable: true };
    case 'qr':
      return { type: 'qr', w: 24, h: 24, data: '{{MaterialCode}}', errorCorrection: 'M' };
    case 'logo':
      return { type: 'logo', w: 24, h: 24 };
    case 'line':
      return { type: 'line', w: 60, h: 2, borderWidth: 2, borderColor: '#414752' };
    case 'rectangle':
      return { type: 'rectangle', w: 60, h: 40, borderWidth: 2, borderColor: '#414752' };
    case 'ellipse':
      return { type: 'rectangle', shape: 'ellipse', w: 30, h: 30, borderWidth: 2, borderColor: '#414752' };
    case 'dynamic':
      // Starts empty — the Properties Panel's "Bind Material Field" picker is how a token
      // actually gets added now, rather than guessing {{MaterialName}}.
      return { type: 'text', w: 60, h: 12, text: '', fontSize: 12, fontWeight: 400, align: 'left' };
    case 'dateTime':
      return { type: 'text', w: 50, h: 12, text: '{{PrintDate}}', fontSize: 12, fontWeight: 400, align: 'left' };
    case 'serialNumber':
      // Same AutoGenerateConfig the Barcode/QR "Auto-Generate Value" toggle uses — a serial
      // number object is a text object with this set, resolved to a real reserved value at
      // print time exactly like an auto-generating barcode's data is.
      return { type: 'text', w: 40, h: 12, text: '', fontSize: 12, fontWeight: 400, align: 'left', autoGenerate: DEFAULT_AUTO_GENERATE_CONFIG };
    case 'table':
      return { type: 'table', w: 70, h: 40, fields: [], rowHeight: 10, fontSize: 11, showLabels: true };
    case 'text':
    default:
      return { type: 'text', w: 60, h: 12, text: 'New Text', fontSize: 12, fontWeight: 400, align: 'left' };
  }
}

function createEmptyTemplate(): LabelTemplate {
  return {
    id: `tpl-${Date.now()}`,
    name: 'Untitled_Template',
    description: '',
    widthMm: 100,
    heightMm: 60,
    updatedDate: new Date().toISOString(),
    updatedBy: 'You',
    status: 'Draft',
    materialId: null,
    assignmentType: 'Generic',
    objects: [],
    hasBeenPrinted: false,
  };
}

export default function LabelTemplateDesigner() {
  const { t } = useTranslation('labelDesigner');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const isNew = id === 'new';
  const canAct = hasPermission(PERMISSIONS.APPROVAL_MATRIX_ACT);

  const [template, setTemplate] = useState<LabelTemplate>(createEmptyTemplate());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (isNew) {
      setTemplate(createEmptyTemplate());
      return;
    }
    apiClient.get<LabelTemplate>(`/label-templates/${id}`).then(({ data }) => {
      if (data) setTemplate(data);
    });
  }, [id, isNew]);

  const selectedObject = template.objects.find((o) => o.id === selectedId) || null;

  const handleAddObject = useCallback((type: ToolboxObjectType) => {
    const base = createDefaultObject(type);
    const newObject = { id: nextObjectId(), x: 10, y: 10, ...base } as LabelObject;
    setTemplate((prev) => ({ ...prev, objects: [...prev.objects, newObject] }));
    setSelectedId(newObject.id);
  }, []);

  // Drag-and-drop from the toolbox: the dropped point becomes the object's center rather than
  // its top-left corner, so it lands directly under the cursor the way a user visually expects
  // instead of appearing offset down-and-right by half its own size.
  const handleDropObject = useCallback((type: ToolboxObjectType, xMm: number, yMm: number) => {
    const base = createDefaultObject(type);
    const newObject = { id: nextObjectId(), x: Math.max(0, xMm - base.w / 2), y: Math.max(0, yMm - base.h / 2), ...base } as LabelObject;
    setTemplate((prev) => ({ ...prev, objects: [...prev.objects, newObject] }));
    setSelectedId(newObject.id);
  }, []);

  const handleMove = useCallback((objId: string, dx: number, dy: number) => {
    setTemplate((prev) => ({
      ...prev,
      objects: prev.objects.map((o) => (o.id === objId ? { ...o, x: Math.max(0, o.x + dx), y: Math.max(0, o.y + dy) } : o)),
    }));
  }, []);

  const handlePropertyChange = useCallback((objId: string, patch: Record<string, unknown>) => {
    setTemplate((prev) => ({
      ...prev,
      objects: prev.objects.map((o) => (o.id === objId ? ({ ...o, ...patch } as LabelObject) : o)),
    }));
  }, []);

  const handleDeleteObject = useCallback((objId: string) => {
    setTemplate((prev) => ({ ...prev, objects: prev.objects.filter((o) => o.id !== objId) }));
    setSelectedId(null);
  }, []);

  const handleTemplateMetaChange = useCallback((patch: Partial<LabelTemplate>) => {
    setTemplate((prev) => ({ ...prev, ...patch }));
  }, []);

  const saveMutation = useMutation({
    mutationFn: (payload: LabelTemplate) => apiClient.post<LabelTemplate>('/label-templates', payload),
    onSuccess: ({ data: saved }) => {
      setTemplate(saved);
      if (isNew) navigate(`/label-designer/${saved.id}`, { replace: true });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (templateId: string) => apiClient.delete(`/label-templates/${templateId}`),
    onSuccess: () => {
      setConfirmDelete(false);
      navigate('/label-designer');
    },
  });

  // Same GET /approvals/pending query key as NotificationBell.tsx — shares its cache, and reusing
  // it (rather than a template-specific endpoint) means this bar and the header bell always agree.
  const { data: pendingApprovals = [] } = useQuery({
    queryKey: ['approvals', 'pending'],
    queryFn: async () => (await apiClient.get<ApprovalInstanceDto[]>('/approvals/pending')).data,
    enabled: canAct && !isNew,
  });
  const pendingInstance = pendingApprovals.find((i) => i.transactionType === 'LabelTemplate' && i.transactionId === template.id);

  const actMutation = useMutation({
    mutationFn: ({ instanceId, decision }: { instanceId: string; decision: 'Approved' | 'Rejected' }) =>
      apiClient.post(`/approvals/${instanceId}/action`, { decision, comments: null }),
    onSuccess: (response) => {
      const status = (response.data as { status?: string } | undefined)?.status;
      setTemplate((prev) => ({ ...prev, status: status === 'Rejected' ? 'Rejected' : 'Active' }));
    },
    // Refetch on failure too, not just success — a "this approval has already been actioned" error
    // means our cached pending-list is stale (someone else, or a lost race from a double-click,
    // already resolved it), and without this the bar would keep showing with working-looking
    // buttons that fail the exact same way forever.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['approvals', 'pending'] });
      queryClient.invalidateQueries({ queryKey: ['label-templates'] });
    },
  });

  // Same synchronous double-click guard as NotificationBell.tsx — actMutation.isPending flips
  // asynchronously, which isn't fast enough to stop a real double-click from firing twice.
  const actInFlight = useRef(false);
  const act = (decision: 'Approved' | 'Rejected') => {
    if (!pendingInstance || actInFlight.current) return;
    actInFlight.current = true;
    actMutation.mutate(
      { instanceId: pendingInstance.id, decision },
      { onSettled: () => { actInFlight.current = false; } },
    );
  };

  const handleDuplicate = () => {
    setTemplate((prev) => ({ ...createEmptyTemplate(), ...prev, id: `tpl-${Date.now()}`, name: `${prev.name}_copy` }));
  };

  const handleDeleteTemplate = () => {
    if (isNew) {
      setConfirmDelete(false);
      navigate('/label-designer');
      return;
    }
    deleteMutation.mutate(template.id);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 88px)', mx: -4, mt: -4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 3, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Breadcrumbs>
          <Link underline="hover" color="inherit" sx={{ cursor: 'pointer' }} onClick={() => navigate('/label-designer')}>
            {t('designer.templatesBreadcrumb')}
          </Link>
          <Typography color="text.primary" sx={{ fontWeight: 600 }}>
            {template.name}
          </Typography>
        </Breadcrumbs>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={() => saveMutation.mutate(template)} disabled={saveMutation.isPending}>
          {saveMutation.isPending ? t('designer.saving') : t('designer.saveTemplate')}
        </Button>
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 3, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" spacing={1}>
          <Button size="small" startIcon={<ContentCopyIcon />} onClick={handleDuplicate}>
            {t('designer.duplicate')}
          </Button>
          {/* Hidden once real labels have been printed from this template — its Id is already
              referenced by print history, so deleting it here would leave that history dangling
              (DeleteAsync refuses the request server-side too, this is just the UI mirror). */}
          {!template.hasBeenPrinted && (
            <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => setConfirmDelete(true)}>
              {t('designer.delete')}
            </Button>
          )}
        </Stack>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <ToggleButtonGroup size="small" value={null} exclusive>
            <ToggleButton value="zoomOut" onClick={() => setZoom((z) => Math.max(0.5, z - 0.1))}>
              <ZoomOutIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="reset" onClick={() => setZoom(1)} sx={{ px: 2 }}>
              {Math.round(zoom * 100)}%
            </ToggleButton>
            <ToggleButton value="zoomIn" onClick={() => setZoom((z) => Math.min(2, z + 0.1))}>
              <ZoomInIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
          <Divider orientation="vertical" flexItem />
          <Button size="small" variant="outlined" onClick={() => navigate('/transactions/label-printing')}>
            {t('designer.goToPrinting')}
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Toolbox onAddObject={handleAddObject} />
        <LabelCanvas
          objects={template.objects}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onMove={handleMove}
          onResize={handlePropertyChange}
          onDropObject={handleDropObject}
          widthMm={template.widthMm}
          heightMm={template.heightMm}
          zoom={zoom}
        />
        <PropertiesPanel object={selectedObject} onChange={handlePropertyChange} onDelete={handleDeleteObject} template={template} onTemplateMetaChange={handleTemplateMetaChange} />
      </Box>

      {pendingInstance && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}
        >
          <Alert severity="warning" sx={{ flex: 1, py: 0 }}>
            {t('designer.approvalBar.message')}
          </Alert>
          <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0 }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleOutlinedIcon />}
              disabled={actMutation.isPending}
              onClick={() => act('Approved')}
            >
              {t('designer.approvalBar.approve')}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelOutlinedIcon />}
              disabled={actMutation.isPending}
              onClick={() => act('Rejected')}
            >
              {t('designer.approvalBar.reject')}
            </Button>
          </Stack>
        </Stack>
      )}

      <ConfirmDialog
        open={confirmDelete}
        title={t('designer.deleteDialog.title')}
        message={t('designer.deleteDialog.message', { name: template.name })}
        confirmLabel={t('designer.deleteDialog.confirm')}
        confirmColor="error"
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDeleteTemplate}
        loading={deleteMutation.isPending}
      />
    </Box>
  );
}
